import { useCallback, useEffect, useRef } from "react";
import jwtDecode from "jwt-decode";

const GOOGLE_CLIENT_ID =
  "341935228570-l3uovc1akibqn63tjf2lfhiaamvnuk61.apps.googleusercontent.com";

/**
 * Google Identity Services (GIS) sign-in hook.
 *
 * Replaces the deprecated `gapi.auth2` access-token flow. GIS returns an ID
 * token (a signed JWT "credential") via a callback. The backend
 * `POST /api/auth/with-google` accepts that credential in the `token` field
 * and derives the user's email/profile from it server-side.
 *
 * Because the project uses its own custom-styled "Continue with Google"
 * buttons (not Google's rendered button), we render an invisible GIS button
 * off-screen and programmatically click it when the visible button is pressed.
 * This is the most reliable click-triggered path to obtain an ID token across
 * browsers (One Tap / `prompt()` can be silently suppressed).
 *
 * @param {(payload: { credential: string, email?: string, name?: string, picture?: string }) => void} onCredential
 *        Called with the ID token credential (and decoded profile fields) on success.
 * @returns {{ signIn: () => void }} `signIn` triggers the Google sign-in flow.
 */
const useGoogleSignIn = (onCredential) => {
  const hiddenButtonRef = useRef(null);
  const callbackRef = useRef(onCredential);

  // Keep the latest callback without re-initializing GIS on every render.
  useEffect(() => {
    callbackRef.current = onCredential;
  }, [onCredential]);

  useEffect(() => {
    const initialize = () => {
      if (!window.google || !window.google.accounts || !window.google.accounts.id) {
        return false;
      }

      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (response) => {
          if (!response || !response.credential) return;

          let decoded = {};
          try {
            decoded = jwtDecode(response.credential);
          } catch (error) {
            // A malformed credential should not crash the flow; the backend
            // re-verifies the token regardless.
            console.error("Failed to decode Google credential:", error);
          }

          callbackRef.current({
            credential: response.credential,
            email: decoded.email,
            name: decoded.name,
            picture: decoded.picture,
          });
        },
      });

      // Render an off-screen Google button we can click programmatically so
      // the visible custom button keeps its existing look and behavior.
      if (hiddenButtonRef.current) {
        window.google.accounts.id.renderButton(hiddenButtonRef.current, {
          type: "standard",
          theme: "outline",
          size: "large",
        });
      }

      return true;
    };

    // The GIS script (accounts.google.com/gsi/client) loads async/defer, so it
    // may not be ready on first render. Poll briefly until it is.
    if (initialize()) return;

    const intervalId = setInterval(() => {
      if (initialize()) clearInterval(intervalId);
    }, 200);

    return () => clearInterval(intervalId);
  }, []);

  const signIn = useCallback(() => {
    const googleButton =
      hiddenButtonRef.current &&
      hiddenButtonRef.current.querySelector("div[role=button], button");

    if (googleButton) {
      googleButton.click();
    } else if (
      window.google &&
      window.google.accounts &&
      window.google.accounts.id
    ) {
      // Fallback: attempt the One Tap / prompt flow if the rendered button is
      // unavailable for any reason.
      window.google.accounts.id.prompt();
    } else {
      console.error("Google Identity Services is not loaded yet.");
    }
  }, []);

  return { signIn, hiddenButtonRef };
};

export default useGoogleSignIn;
